import { z } from 'zod';
// ---- Auth Schemas ----
export const registerSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[0-9]/, 'Password must contain at least one number'),
    name: z.string().min(2, 'Name must be at least 2 characters').max(100),
    companyName: z.string().min(2, 'Company name must be at least 2 characters').max(200).optional(),
    inviteToken: z.string().min(8).max(200).optional(),
}).superRefine((data, ctx) => {
    if (!data.inviteToken && !data.companyName) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['companyName'],
            message: 'Company name is required',
        });
    }
});
export const loginSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(1, 'Password is required'),
});
export const forgotPasswordSchema = z.object({
    email: z.string().email('Invalid email address'),
});
export const resetPasswordSchema = z.object({
    token: z.string().min(1),
    password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[0-9]/, 'Password must contain at least one number'),
});
export const inviteSchema = z.object({
    email: z.string().email('Invalid email address'),
    role: z.enum(['admin', 'viewer']),
});
export const updateInviteSchema = z.object({
    role: z.enum(['admin', 'viewer']).optional(),
    expiresAt: z.string().datetime().optional(),
});
// ---- Company Schemas ----
/**
 * Bitrix24 incoming-webhook URL:
 *   https://<portal>.bitrix24.<tld>/rest/<userId>/<secret>/
 * <portal> may include digits, lowercase letters, and hyphens (e.g.
 * `b24-q42kz4`). <tld> covers the public Bitrix24 zones.
 *
 * The URL embeds the auth secret — it's the credential, not a public URL.
 */
export const bitrixWebhookUrlRegex = /^https:\/\/[a-z0-9][a-z0-9-]*\.bitrix24\.(ru|com|de|fr|pl|it|es|kz|by|ua)\/rest\/\d+\/[a-z0-9]+\/?$/i;
export const crmTypeSchema = z.enum(['amocrm', 'bitrix24']);
export const updateCompanySchema = z
    .object({
    name: z.string().min(2).max(200).optional(),
    timezone: z.string().optional(),
    logoUrl: z.string().url().optional().nullable(),
    crmType: crmTypeSchema.optional(),
    crmSubdomain: z
        .string()
        .min(2)
        .max(100)
        .regex(/^[a-z0-9-]+$/, 'Only lowercase letters, numbers, and hyphens')
        .optional()
        .nullable(),
    bitrixWebhookUrl: z
        .string()
        .url()
        .regex(bitrixWebhookUrlRegex, 'Must be a Bitrix24 incoming-webhook URL: https://<portal>.bitrix24.<tld>/rest/<userId>/<secret>/')
        .optional()
        .nullable(),
})
    .superRefine((data, ctx) => {
    // Switching crmType to bitrix24 requires the webhook URL in the same
    // PATCH — otherwise we'd persist a row that violates the DB CHECK.
    if (data.crmType === 'bitrix24' && data.bitrixWebhookUrl == null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'bitrixWebhookUrl is required when crmType is bitrix24',
            path: ['bitrixWebhookUrl'],
        });
    }
    if (data.crmType === 'amocrm' && data.bitrixWebhookUrl != null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'bitrixWebhookUrl is only allowed when crmType is bitrix24',
            path: ['bitrixWebhookUrl'],
        });
    }
});
// ---- AI Settings Schemas ----
export const updateAISettingsSchema = z.object({
    systemPrompt: z.string().min(10, 'System prompt must be at least 10 characters').max(10000).optional(),
    maxMessages: z.number().int().min(1).max(50).optional(),
    hardStopThreshold: z.number().int().min(1).max(10).optional(),
    responseMaxLength: z.number().int().min(50).max(4000).optional(),
    blockedTopics: z.array(z.string()).optional(),
    strictRules: z.array(z.string()).optional(),
    messagesContextAmount: z.number().int().min(1).max(20).optional(),
    consultPhraseTemplate: z.string().max(1000).optional(),
    amoGatewayWorkflowId: z.string().max(255).optional().nullable(),
    googleCalendarId: z.string().max(500).optional().nullable(),
    needCalendar: z.boolean().optional(),
    needConsultationOption: z.boolean().optional(),
    // Lead rollout / test mode: 100 and empty lists = the bot handles every lead.
    rolloutPercent: z.number().int().min(0).max(100).optional(),
    rolloutStatusIds: z.array(z.number().int().positive()).max(100).optional(),
    rolloutTags: z.array(z.string().trim().min(1).max(100)).max(100).optional(),
    // Company test mode: end of the window (null = off); the maximum window is checked by the API.
    testModeUntil: z.string().datetime().nullable().optional(),
    // Greeting for /start outside test mode (null or empty = default from code).
    welcomeMessage: z.string().max(1000).nullable().optional(),
});
// ---- Conversations Schemas ----
export const conversationFiltersSchema = z.object({
    status: z.enum(['active', 'completed', 'handed_off', 'abandoned']).optional(),
    intent: z.string().optional(),
    channel: z.string().optional(),
    dateFrom: z.string().datetime().optional(),
    dateTo: z.string().datetime().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(10).max(100).default(20),
});
// ---- Analytics Schemas ----
export const analyticsFiltersSchema = z.object({
    dateFrom: z.string().datetime().optional(),
    dateTo: z.string().datetime().optional(),
    channel: z.string().optional(),
    granularity: z.enum(['day', 'week', 'month']).default('day'),
});
//# sourceMappingURL=index.js.map